import React, { useLayoutEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { Field, change, formValueSelector } from 'redux-form';
import { connect } from 'react-redux';
import { FormGroup } from 'reactstrap';
import { toastr } from 'react-redux-toastr';
import { isRequired } from '../../../../utils/validators';
import {
  getItineraryFoodGroup,
  deselectSeat,
  createCustomerFromPerson,
} from '../../../../actions/';
import { DATE_TIME_FORMAT } from '../../../../config/locale';
import Select from '../../../common/forms/select/Select';
import { orderBy } from '../../../../utils/array';
import FormItem from '../../../common/forms/FormItem';
import { tzNormalizeDate } from '../../../../utils/date';
import PersonInputGroup from '../../../common/forms/PersonInputGroup';

const hasValidSeat = ({ value }, allValues) => {
  let duplicateCount = 0;
  allValues.passengers.forEach((passenger) => {
    const { seatReservation } = passenger;
    if (seatReservation && seatReservation.value === value) {
      duplicateCount += 1;
    }
  });

  return duplicateCount >= 2 ? 'Asiento repetido' : undefined;
};

const FORM_NAME = 'rideBookingPassengerForm';

const PERSON_SOURCE = 'PERSON';

const CUSTOMER_SOURCE = 'CUSTOMER';

const selector = formValueSelector(FORM_NAME);

// same option shape PersonSelect builds for a CUSTOMER result
const toCustomerOption = (customer, person) => {
  const firstName = customer.firstName || person.firstName || '';
  const lastName = customer.lastName || person.lastName || '';
  const idDocumentNumber = customer.idDocumentNumber || person.idDocumentNumber;
  const identificationTypeId = customer.identificationType
    ? customer.identificationType.id
    : person.identificationTypeId;
  const identificationTypeName = customer.identificationType
    ? customer.identificationType.name
    : person.identificationTypeName;
  const idCountryOfOrigin =
    customer.idCountryOfOrigin || person.idCountryOfOrigin;

  return {
    ...person,
    value: `${customer.id}-${CUSTOMER_SOURCE}`,
    label: `${firstName} ${lastName} | ${identificationTypeName}: ${idDocumentNumber} (${idCountryOfOrigin})`,
    id: customer.id,
    source: CUSTOMER_SOURCE,
    firstName,
    lastName,
    idDocumentNumber,
    identificationTypeId,
    identificationTypeName,
    idCountryOfOrigin,
    dob: customer.dob || person.dob,
    mobilePhone: customer.mobilePhone || person.mobilePhone,
  };
};

const PassengerForm = ({
  index,
  passenger,
  foodOptions,
  seatReservations,
  dispatchGetItineraryFoodGroup,
  itineraryId,
  dispatchDeselectSeat,
  fields,
  seatSelectionChangesInProgress,
  dispatchChange,
  dispatchCreateCustomerFromPerson,
  fieldsLength,
  hasPinpadCharge,
  passengerValue,
}) => {
  // latest value, readable after the async PERSON conversion
  const passengerValueRef = useRef(passengerValue);
  passengerValueRef.current = passengerValue;

  useLayoutEffect(() => {
    if (itineraryId) dispatchGetItineraryFoodGroup(itineraryId);
  }, []);

  const onChangePassenger = async (person) => {
    if (!person || person.source !== PERSON_SOURCE) return;

    const customer = await dispatchCreateCustomerFromPerson(person.id);

    if (!customer) return;

    const currentPassenger = passengerValueRef.current;
    if (!currentPassenger || currentPassenger.value !== person.value) return;

    dispatchChange(
      FORM_NAME,
      `${passenger}.passenger`,
      toCustomerOption(customer, person),
    );
  };

  const onClickDeselectSeat = () => {
    const {
      value: seatReservationId,
      label: seatId,
      itineraryId: seatItineraryId,
    } = seatReservations[index];

    fields.remove(index);

    dispatchDeselectSeat({
      seatReservationId,
      itineraryId: seatItineraryId,
      seatId,
      isRideBooking: true,
    });
  };

  let foodInput = null;
  if (foodOptions) {
    foodInput = (
      <FormGroup row>
        <FormItem label="Alimento" required>
          <Field
            name={`${passenger}.food`}
            component={Select}
            options={foodOptions}
            validate={[isRequired]}
          />
        </FormItem>
      </FormGroup>
    );
  }

  let route = null;
  let deleteButton = null;
  if (seatReservations[index]) {
    const {
      itinerary: {
        originName,
        destinationName,
        serviceName,
        departureTime,
        arrivalTime,
      },
    } = seatReservations[index];

    const formattedDepartureTime = tzNormalizeDate({
      date: departureTime,
      format: DATE_TIME_FORMAT,
    });

    const formattedArrivalTime = tzNormalizeDate({
      date: arrivalTime,
      format: DATE_TIME_FORMAT,
    });

    route = (
      <>
        <div className="mr-2">({serviceName})</div>
        <div className="text-center">
          {originName}{' '}
          <p>
            <small>{formattedDepartureTime}</small>
          </p>
        </div>
        <div className="mr-3 ml-3"> &rarr; </div>
        <div className="text-center">
          {destinationName}{' '}
          <p>
            <small>{formattedArrivalTime}</small>
          </p>
        </div>
      </>
    );

    let deselectSeatFunction = null;

    if (hasPinpadCharge) {
      // removing a seat changes the total of an already charged sale
      deselectSeatFunction = () =>
        toastr.warning(
          'Advertencia',
          'Cancele el cobro en el pinpad antes de quitar un asiento.',
        );
    } else if (seatSelectionChangesInProgress.size === 0) {
      deselectSeatFunction = onClickDeselectSeat;
    }

    deleteButton = (
      <i
        className="fa fa-times fa-2x text-danger clickable"
        role="button"
        onClick={deselectSeatFunction}
        onKeyPress={deselectSeatFunction}
        tabIndex={0}
      />
    );
  }

  const header = (
    <div className="clearfix">
      <div className="float-left">
        <h5>
          <div className="d-flex">
            <div className="mr-3">Pasajero {index + 1}: </div> {route}
          </div>
        </h5>
      </div>
      <div className="float-right">{deleteButton}</div>
    </div>
  );

  return (
    <div>
      <hr />
      {header}
      <PersonInputGroup
        label="Pasajero"
        name={`${passenger}.passenger`}
        form={FORM_NAME}
        onChange={onChangePassenger}
        index={index}
        fieldsLength={fieldsLength}
      />

      <FormGroup row>
        <FormItem label="Asiento" required>
          <Field
            name={`${passenger}.seatReservation`}
            component={Select}
            options={seatReservations}
            isDisabled
            validate={[isRequired, hasValidSeat]}
          />
        </FormItem>
      </FormGroup>
      {foodInput}
    </div>
  );
};

PassengerForm.propTypes = {
  index: PropTypes.number.isRequired,
  passenger: PropTypes.string.isRequired,
  foodOptions: Select.propTypes.options,
  seatReservations: Select.propTypes.options.isRequired,
  dispatchGetItineraryFoodGroup: PropTypes.func.isRequired,
  itineraryId: PropTypes.number,
  dispatchDeselectSeat: PropTypes.func.isRequired,
  // eslint-disable-next-line react/forbid-prop-types
  fields: PropTypes.object.isRequired,
  seatSelectionChangesInProgress: PropTypes.instanceOf(Immutable.Set)
    .isRequired,
  dispatchChange: PropTypes.func.isRequired,
  dispatchCreateCustomerFromPerson: PropTypes.func.isRequired,
  passengerValue: PropTypes.shape({
    value: PropTypes.string,
    source: PropTypes.string,
  }),
  fieldsLength: PropTypes.number.isRequired,
  hasPinpadCharge: PropTypes.bool.isRequired,
};

PassengerForm.defaultProps = {
  foodOptions: null,
  itineraryId: null,
  passengerValue: null,
};

const mapStateToProps = (state, { index }) => {
  const seatReservations = state.BookingUnit.Booking.getIn([
    'seats',
    'seatReservations',
  ])
    .map((seatReservation) => ({
      value: seatReservation.id,
      label: seatReservation.seat.seatNumber,
      itinerary: seatReservation.itinerary || null,
      itineraryId: seatReservation.itineraryId || null,
      seatNumber: seatReservation.seat.seatNumber,
      floorNumber: seatReservation.seat.floorNumber,
    }))
    .toJS();

  const seatReservationsOrdered = orderBy(
    seatReservations,
    ['itinerary.departureTime', 'itineraryId', 'label'],
    ['asc'],
  );

  const foodGroup = state.BookingUnit.Booking.getIn([
    'passengers',
    'itineraryFoodGroup',
  ]);

  let foodOptions = null;
  if (foodGroup && foodGroup.foodSet) {
    foodOptions = foodGroup.foodSet.map((food) => ({
      value: food.id,
      label: food.name,
    }));
  }

  const seatSelectionChangesInProgress = state.BookingUnit.Booking.getIn([
    'seats',
    'seatSelectionChangesInProgress',
  ]);

  // same order as the form passengers, built in RideBookingPassengerForm
  const currentSeatReservation = seatReservationsOrdered[index];

  return {
    seatReservations: seatReservationsOrdered,
    foodOptions,
    itineraryId: currentSeatReservation
      ? currentSeatReservation.itineraryId
      : null,
    seatSelectionChangesInProgress,
    passengerValue: selector(state, `passengers[${index}].passenger`),
    hasPinpadCharge: (selector(state, 'transactionList') || []).some(
      (row) => row && row.pinpadPaid,
    ),
  };
};

const mapDispatchToProps = {
  dispatchGetItineraryFoodGroup: getItineraryFoodGroup,
  dispatchChange: change,
  dispatchDeselectSeat: deselectSeat,
  dispatchCreateCustomerFromPerson: createCustomerFromPerson,
};

export default connect(mapStateToProps, mapDispatchToProps)(PassengerForm);
